import React, { useState, useEffect } from 'react';
import { X, CheckCircle, Plus, RefreshCw, Building, Shield } from 'lucide-react';
import toast from '../../../../utils/toast';
import Portal from '../../../../components/UI/Portal';

const TrainingProviderForm = ({ provider, isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    uen: '',
    name: '',
    trading_name: '',
    provider_type: 'private',
    contact_person: '',
    contact_email: '',
    contact_phone: '',
    website_url: '',
    registered_address: '',
    safety_training_approved: false,
    skillsfuture_eligible: false,
    is_active: true,  // New field for soft delete
    notes: ''  // New field for HR admin notes
  });
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);

  useEffect(() => {
    if (provider) {
      setFormData({
        uen: provider.uen || '',
        name: provider.name || '',
        trading_name: provider.trading_name || '',
        provider_type: provider.provider_type || 'private',
        contact_person: provider.contact_person || '',
        contact_email: provider.contact_email || '',
        contact_phone: provider.contact_phone || '',
        website_url: provider.website_url || '',
        registered_address: provider.registered_address || '',
        safety_training_approved: provider.safety_training_approved || false,
        skillsfuture_eligible: provider.skillsfuture_eligible || false,
        is_active: provider.is_active !== undefined ? provider.is_active : true,
        notes: provider.notes || ''
      });
    } else {
      setFormData({
        uen: '',
        name: '',
        trading_name: '',
        provider_type: 'private',
        contact_person: '',
        contact_email: '',
        contact_phone: '',
        website_url: '',
        registered_address: '',
        safety_training_approved: false,
        skillsfuture_eligible: false,
        is_active: true,
        notes: ''
      });
    }
    setErrors({});
  }, [provider, isOpen]);

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.uen) newErrors.uen = 'UEN is required';
    if (!formData.name) newErrors.name = 'Provider name is required';
    if (!formData.registered_address || formData.registered_address.trim() === '') {
      newErrors.registered_address = 'Registered address is required';
    }

    // UEN format validation (basic)
    if (formData.uen && !/^\d{8}[A-Z]$|^\d{9}[A-Z]$|^[ST]\d{7}[A-Z]$|^T\d{2}[A-Z]{2}\d{4}[A-Z]$/.test(formData.uen)) {
      newErrors.uen = 'Invalid UEN format';
    }

    // Email validation
    if (formData.contact_email && !/\S+@\S+\.\S+/.test(formData.contact_email)) {
      newErrors.contact_email = 'Invalid email format';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) return;

    setSaving(true);
    try {
      const url = provider 
        ? `/api/v1/configuration/training-providers/${provider.uen}/`
        : '/api/v1/configuration/training-providers/';
      
      const method = provider ? 'PUT' : 'POST';
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      if (!response.ok) {
        const errorData = await response.json();
        const fieldErrors = {};
        
        // Handle field-level validation errors from Django REST Framework
        // DRF returns errors as: { "field_name": ["error message"] }
        Object.keys(errorData).forEach(key => {
          if (Array.isArray(errorData[key])) {
            fieldErrors[key] = errorData[key][0];
          } else if (typeof errorData[key] === 'string') {
            fieldErrors[key] = errorData[key];
          }
        });
        
        // If we have field errors, set them and show a general message
        if (Object.keys(fieldErrors).length > 0) {
          setErrors(fieldErrors);
          throw new Error('Please fix the validation errors');
        }
        
        // Handle general error message
        const errorMessage = errorData.detail || 
          errorData.error || 
          (typeof errorData === 'string' ? errorData : 'Failed to save provider');
        throw new Error(errorMessage);
      }

      const savedProvider = await response.json();
      onSave && onSave(savedProvider);
      onClose();
    } catch (error) {
      console.error('Error saving provider:', error);
      setErrors({ submit: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleUENVerification = async (uen) => {
    if (!uen || uen.length < 8) return;
    
    try {
      setVerifying(true);
      
      const response = await fetch(`/api/v1/configuration/uen-verification/verify/${uen}/`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('accessToken') || sessionStorage.getItem('accessToken')}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const result = await response.json();
      
      if (result.verified) {
        setFormData(prev => ({
          ...prev,
          name: result.entity_name || prev.name,
          registered_address: result.registered_address || prev.registered_address,
        }));
        
        setErrors(prev => ({ ...prev, uen: null }));
        toast.success(`✅ UEN verified! Company: ${result.entity_name}`);
      } else {
        setErrors(prev => ({
          ...prev,
          uen: result.error || 'UEN not found in government database'
        }));
      }
      
    } catch (error) {
      console.error('UEN verification error:', error);
      setErrors(prev => ({
        ...prev,
        uen: 'Verification failed. Please try again.'
      }));
    } finally {
      setVerifying(false);
    }
  };

  if (!isOpen) return null;

  return (
    <Portal>
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-container modal-container-lg" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3 className="modal-title flex items-center gap-2">
            <Building className="w-5 h-5 text-red-600" />
            {provider ? 'Edit Training Provider' : 'Add New Training Provider'}
          </h3>
          <button onClick={onClose} className="modal-close-admin">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
          <div className="modal-content">
            {errors.submit && (
              <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg flex items-center gap-2">
                <X className="w-4 h-4" />
                {errors.submit}
              </div>
            )}

            <div className="space-y-6">
          {/* UEN Section */}
          <div className="bg-gray-50 p-4 rounded-lg">
            <h4 className="text-md font-medium text-gray-900 mb-3">Company Identification</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  UEN Number *
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={formData.uen}
                    onChange={(e) => setFormData({...formData, uen: e.target.value.toUpperCase()})}
                    className={`input-admin flex-1 ${errors.uen ? 'border-red-500' : ''}`}
                    placeholder="e.g., 200102574Z"
                    maxLength={10}
                    disabled={!!provider}
                  />
                  {!provider && (
                    <button
                      type="button"
                      onClick={() => handleUENVerification(formData.uen)}
                      disabled={!formData.uen || formData.uen.length < 8 || verifying}
                      className="btn btn-admin-secondary"
                    >
                      {verifying ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Verifying
                        </>
                      ) : (
                        <>
                          <Shield className="w-4 h-4 mr-2" />
                          Verify UEN
                        </>
                      )}
                    </button>
                  )}
                </div>
                {errors.uen && <p className="text-red-500 text-sm mt-1">{errors.uen}</p>}
                <p className="text-gray-500 text-xs mt-1">
                  Enter UEN to auto-fill company details from ACRA
                </p>
              </div>
            </div>
          </div>

          {/* Basic Information */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Provider Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Official Name (ACRA) *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`input-admin w-full ${errors.name ? 'border-red-500' : ''}`}
                  placeholder="Official registered name"
                />
                {errors.name && <p className="text-red-500 text-sm mt-1">{errors.name}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trading Name
                </label>
                <input
                  type="text"
                  value={formData.trading_name}
                  onChange={(e) => setFormData({...formData, trading_name: e.target.value})}
                  className="input-admin w-full"
                  placeholder="Trading name (if different)"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Provider Type
                </label>
                <select
                  value={formData.provider_type}
                  onChange={(e) => setFormData({...formData, provider_type: e.target.value})}
                  className="select-admin w-full"
                >
                  <option value="private">Private Training Provider</option>
                  <option value="government">Government Agency</option>
                  <option value="industry">Industry Association</option>
                  <option value="public">Public Institution</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Website
                </label>
                <input
                  type="url"
                  value={formData.website_url}
                  onChange={(e) => setFormData({...formData, website_url: e.target.value})}
                  className="input-admin w-full"
                  placeholder="https://www.provider.com"
                />
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div>
            <h4 className="text-md font-medium text-gray-900 mb-3">Contact Information</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Contact Person
                </label>
                <input
                  type="text"
                  value={formData.contact_person}
                  onChange={(e) => setFormData({...formData, contact_person: e.target.value})}
                  className="input-admin w-full"
                  placeholder="Primary contact"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.contact_email}
                  onChange={(e) => setFormData({...formData, contact_email: e.target.value})}
                  className={`input-admin w-full ${errors.contact_email ? 'border-red-500' : ''}`}
                  placeholder="contact@provider.com"
                />
                {errors.contact_email && <p className="text-red-500 text-sm mt-1">{errors.contact_email}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Phone
                </label>
                <input
                  type="tel"
                  value={formData.contact_phone}
                  onChange={(e) => setFormData({...formData, contact_phone: e.target.value})}
                  className="input-admin w-full"
                  placeholder="+65 6123 4567"
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Registered Address *
              </label>
              <textarea
                value={formData.registered_address}
                onChange={(e) => setFormData({...formData, registered_address: e.target.value})}
                rows={2}
                className={`input-admin w-full ${errors.registered_address ? 'border-red-500' : ''}`}
                placeholder="Full registered address"
              />
              {errors.registered_address && (
                <p className="text-red-500 text-sm mt-1">{errors.registered_address}</p>
              )}
            </div>
          </div>

          {/* Approvals */}
          <div className="flex items-center gap-6">
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.safety_training_approved}
                onChange={(e) => setFormData({...formData, safety_training_approved: e.target.checked})}
                className="h-4 w-4 text-red-600 focus-visible:ring-red-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-900">Approved for Safety Training</span>
            </label>
            <label className="flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.skillsfuture_eligible}
                onChange={(e) => setFormData({...formData, skillsfuture_eligible: e.target.checked})}
                className="h-4 w-4 text-red-600 focus-visible:ring-red-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-900">SkillsFuture Eligible</span>
            </label>
          </div>

          {/* Library Management */}
          <div className="bg-gray-50 p-4 rounded-lg mt-4">
            <h4 className="text-md font-medium text-gray-900 mb-3">Library Management</h4>
            <div className="space-y-4">
              <label className="flex items-center cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="h-4 w-4 text-red-600 focus-visible:ring-red-500 border-gray-300 rounded"
                />
                <span className="ml-2 text-sm text-gray-900">Active in Library</span>
              </label>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Internal Notes
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({...formData, notes: e.target.value})}
                  rows={3}
                  className="input-admin w-full"
                  placeholder="HR admin notes (internal use only)"
                />
                <p className="text-gray-500 text-xs mt-1">Notes for HR administrators (not visible to employees)</p>
              </div>
            </div>
          </div>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-admin-outline"
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            className="btn btn-admin-primary"
            disabled={saving}
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <Plus className="w-4 h-4 mr-2" />
                {provider ? 'Update Provider' : 'Add Provider'}
              </>
            )}
          </button>
          </div>
        </form>
      </div>
    </div>
    </Portal>
  );
};

export default TrainingProviderForm;
